import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { usePublicPayment, PublicPaymentMethod } from '../../hooks/usePublicPayment';
import { PaymentMethodLogo, type PaymentMethodKey } from '../../components/ui/PaymentMethodLogo';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function humanStatus(status: string): string {
  const map: Record<string, string> = {
    draft: 'Draft',
    sent: 'Sent',
    response_received: 'Response Received',
    booked: 'Booked',
    declined: 'Declined',
    cancelled: 'Cancelled',
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    completed: 'Completed',
    on_hold: 'On Hold',
  };
  return map[status] ?? status;
}

const METHOD_COLORS: Record<string, string> = {
  venmo: 'bg-[#3D95CE]',
  cashapp: 'bg-[#00D632]',
  paypal: 'bg-[#003087]',
  zelle: 'bg-[#6D1ED4]',
  stripe: 'bg-plum',
};

const DEEP_LINK_METHODS = ['venmo', 'cashapp', 'paypal'];

function methodDisplayName(type: string): string {
  const map: Record<string, string> = {
    venmo: 'Venmo',
    cashapp: 'Cash App',
    paypal: 'PayPal',
    zelle: 'Zelle',
    check: 'Check',
    cash: 'Cash',
    custom: 'Custom',
    stripe: 'Card',
  };
  return map[type] ?? type;
}

// ── Deep link builder (client-side) ─────────────────────────────────────────

function buildDeepLink(method: PublicPaymentMethod, amountCents: number, note: string): string | undefined {
  if (!method.identifier || amountCents <= 0) return undefined;
  const dollars = (amountCents / 100).toFixed(2);
  const encodedNote = encodeURIComponent(note);

  switch (method.type) {
    case 'venmo': {
      const handle = method.identifier.replace(/^@/, '');
      return `https://venmo.com/${encodeURIComponent(handle)}?txn=pay&amount=${dollars}&note=${encodedNote}`;
    }
    case 'cashapp': {
      const tag = method.identifier.startsWith('$') ? method.identifier.slice(1) : method.identifier;
      return `https://cash.app/$${encodeURIComponent(tag)}/${dollars}`;
    }
    case 'paypal': {
      const handle = method.identifier.replace(/^@/, '');
      return `https://www.paypal.com/paypalme/${encodeURIComponent(handle)}/${dollars}`;
    }
    default:
      return undefined;
  }
}

// ── Deep Link Confirmation Modal ─────────────────────────────────────────────

interface DeepLinkModalProps {
  isOpen: boolean;
  method: PublicPaymentMethod | null;
  deepLinkUrl: string | undefined;
  orgName: string;
  onConfirm: () => void;
  onClose: () => void;
}

function DeepLinkModal({ isOpen, method, deepLinkUrl, orgName, onConfirm, onClose }: DeepLinkModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen || !method || !deepLinkUrl) return null;

  const name = methodDisplayName(method.type);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deep-link-modal-title"
    >
      <div
        className="absolute inset-0 bg-midnight/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-6 flex flex-col gap-4">
        <h2
          id="deep-link-modal-title"
          className="font-display text-h3 text-midnight font-semibold"
        >
          You're about to leave this page
        </h2>
        <p className="text-small text-slate leading-relaxed">
          You'll be taken to {name} to complete your payment. Your balance on this page will
          not update immediately — {orgName} will update it once they confirm receipt.
        </p>
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 btn-secondary">
            Cancel
          </button>
          <a
            href={deepLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onConfirm}
            className={`flex-1 btn-base text-white text-center ${METHOD_COLORS[method.type] ?? 'bg-plum'} hover:opacity-90`}
          >
            Continue to {name}
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Copy Button ──────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: do nothing
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="btn-base text-small bg-stone text-midnight hover:bg-slate/30 px-3 py-2 min-h-[44px]"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function CustomerPaymentPage() {
  const { orgId, quoteId } = useParams<{ orgId: string; quoteId: string }>();
  const { data, loading, error } = usePublicPayment(orgId, quoteId);
  const [modalMethod, setModalMethod] = useState<PublicPaymentMethod | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<'due_now' | 'remaining'>('due_now');

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-brand-sm p-6 animate-pulse">
              <div className="h-4 bg-stone rounded w-1/3 mb-4" />
              <div className="h-3 bg-stone rounded w-2/3 mb-2" />
              <div className="h-3 bg-stone rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 404
  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <h1 className="font-display text-h2 text-midnight font-semibold mb-2">Page not found</h1>
          <p className="text-small text-slate">This payment link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  // Error
  if (error || !data) {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <h1 className="font-display text-h2 text-midnight font-semibold mb-2">Something went wrong</h1>
          <p className="text-small text-slate">{error ?? 'Unable to load payment details.'}</p>
        </div>
      </div>
    );
  }

  const { org, customer, job, payment, paymentMethods } = data;
  const dueNow = payment.dueNowAmountCents ?? payment.outstandingAmountCents;
  const remainingBalance = payment.outstandingAmountCents;
  const isPaid = remainingBalance <= 0 && dueNow <= 0;
  const hasSchedule = payment.schedule && payment.schedule.length > 0;
  const totalCost = payment.totalAmountCents;

  // Amount the customer wants to pay
  const payAmountCents = selectedAmount === 'due_now' ? dueNow : remainingBalance;
  const showAmountOptions = !isPaid && dueNow > 0 && remainingBalance > dueNow;

  const stripeMethods = paymentMethods.filter((m) => m.type === 'stripe');
  const offlineMethods = paymentMethods.filter((m) => m.type !== 'stripe');

  // Build deep link URL for the modal based on selected amount
  const modalDeepLinkUrl = modalMethod
    ? buildDeepLink(modalMethod, payAmountCents, `Payment for ${customer.companyName ?? 'move'}`)
    : undefined;

  const handleDeepLinkClick = (method: PublicPaymentMethod) => {
    setModalMethod(method);
  };

  return (
    <div className="min-h-screen bg-mist">
      {/* Org branding header */}
      <div
        className="border-b border-stone"
        style={{
          backgroundColor: org.brandPrimaryColor ?? '#FFFFFF',
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          {org.logo && (
            <img
              src={org.logo}
              alt={`${org.name} logo`}
              className="w-10 h-10 rounded-md object-cover"
            />
          )}
          <span
            className="font-display text-h3 font-semibold"
            style={{ color: org.brandPrimaryColor ? (org.brandTitleTextColor ?? '#FFFFFF') : '#1a1a2e' }}
          >
            {org.name}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left column: Balance & Payment Methods */}
          <div className="space-y-4">
            {/* Balance card */}
            <div className="bg-white rounded-lg shadow-brand-sm border border-stone p-5">
              <h2 className="font-display text-h3 text-midnight font-semibold mb-4">Payment</h2>

              {/* Prominent "Due now" amount */}
              {!isPaid && (
                <div className="text-center mb-4">
                  <span className="block text-small text-slate mb-1">Due now</span>
                  <span className="text-[32px] font-display font-bold text-midnight leading-tight">
                    {formatCents(dueNow)}
                  </span>
                </div>
              )}

              {/* Fully paid state */}
              {isPaid && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center gap-3 mb-4">
                  <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-small text-green-800 font-semibold">
                    No balance due — no additional payment is needed. Thank you!
                  </p>
                </div>
              )}

              {/* Tax breakdown (when tax data present) */}
              {payment.subtotalAmountCents != null && (hasSchedule || !isPaid) && (
                payment.requiresManualTaxReview ? (
                  <div className="border-t border-stone pt-3 space-y-2 mb-1">
                    <div className="flex gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertTriangle size={14} className="shrink-0 text-amber-600 mt-0.5" />
                      <p className="text-[12px] text-amber-800 leading-snug">
                        This is an interstate move. Tax must be determined manually.
                      </p>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-small text-slate">Subtotal</span>
                      <span className="text-body text-midnight">{formatCents(payment.subtotalAmountCents)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-small text-slate">Tax</span>
                      <span className="text-small text-amber-600 font-medium">TBD</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-stone">
                      <span className="text-body font-semibold text-midnight">Total</span>
                      <span className="text-body font-semibold text-midnight">
                        {formatCents(payment.subtotalAmountCents)}
                        <span className="text-[11px] text-slate font-normal ml-1">+ tax</span>
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="border-t border-stone pt-3 space-y-2 mb-1">
                    <div className="flex justify-between items-center">
                      <span className="text-small text-slate">Subtotal</span>
                      <span className="text-body text-midnight">{formatCents(payment.subtotalAmountCents)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-small text-slate">
                        Tax{payment.taxState ? ` (${payment.taxState})` : ''}
                      </span>
                      <span className="text-body text-midnight">
                        {formatCents(payment.taxAmountCents ?? 0)}
                      </span>
                    </div>
                    {payment.laborTaxRate === 0 && payment.taxState && (
                      <p className="text-[11px] text-slate italic">No sales tax applies in {payment.taxState}</p>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-stone">
                      <span className="text-body font-semibold text-midnight">Total</span>
                      <span className="text-body font-semibold text-midnight">{formatCents(totalCost)}</span>
                    </div>
                  </div>
                )
              )}

              {/* Total cost (only when no tax breakdown shown) */}
              {payment.subtotalAmountCents == null && (hasSchedule || !isPaid) && totalCost > 0 && (
                <div className="border-t border-stone pt-3 flex justify-between items-center mb-1">
                  <span className="text-small text-slate">Total Cost</span>
                  <span className="text-body font-semibold text-midnight">
                    {formatCents(totalCost)}
                  </span>
                </div>
              )}

              {/* Payment schedule breakdown */}
              {hasSchedule && (
                <div className="border-t border-stone pt-3 space-y-2">
                  <span className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                    Payment Schedule
                  </span>
                  {payment.schedule.map((item, idx) => {
                    const isPaidItem = item.status === 'paid';
                    const isWaived = item.status === 'waived';
                    const isPartiallyPaid = !isPaidItem && !isWaived && item.remainingCents != null && item.remainingCents < item.amountCents && item.remainingCents > 0;
                    const paidSoFar = item.amountCents - (item.remainingCents ?? item.amountCents);
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between py-2 px-3 rounded-lg border
                          ${isPaidItem
                            ? 'border-emerald-200 bg-emerald-50/30'
                            : item.status === 'due' || item.status === 'overdue'
                              ? 'border-amber-200 bg-amber-50/30'
                              : 'border-stone/60 bg-white'
                          }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isPaidItem ? (
                            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0
                              ${item.status === 'overdue' ? 'border-red-400' : 'border-stone'}`}
                            />
                          )}
                          <div>
                            <span className={`text-small block ${isPaidItem ? 'text-green-700' : isWaived ? 'text-slate line-through' : 'text-midnight'}`}>
                              {item.label}
                            </span>
                            {item.status === 'overdue' && (
                              <span className="text-[11px] font-semibold text-red-500 uppercase">Overdue</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="text-right">
                            {isPartiallyPaid ? (
                              <>
                                <span className="block text-[11px] text-emerald-600 font-medium">
                                  paid: {formatCents(paidSoFar)}
                                </span>
                                <span className="text-small font-semibold text-midnight">
                                  remaining: {formatCents(item.remainingCents ?? item.amountCents)}
                                </span>
                              </>
                            ) : (
                              <span className={`text-small font-semibold ${isPaidItem ? 'text-green-700' : isWaived ? 'text-slate line-through' : 'text-midnight'}`}>
                                {isPaidItem || isWaived
                                  ? formatCents(item.amountCents)
                                  : formatCents(item.remainingCents ?? item.amountCents)}
                              </span>
                            )}
                          </div>
                          {isPaidItem && (
                            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Paid</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Fallback: simple total/paid/balance when no schedule */}
              {!hasSchedule && !isPaid && (
                <div className="border-t border-stone pt-3">
                  {payment.collectedAmountCents > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-small text-slate">Paid</span>
                      <span className="text-body text-green-600 font-semibold">
                        -{formatCents(payment.collectedAmountCents)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Balance due line below schedule */}
              {hasSchedule && (
                <div className="border-t-2 border-midnight/20 mt-3 pt-3 flex justify-between items-center">
                  <span className="text-body text-midnight font-semibold">Balance Due</span>
                  <span className="text-body font-bold text-midnight">
                    {formatCents(remainingBalance)}
                  </span>
                </div>
              )}
            </div>

            {/* Payment methods (only when balance > 0) */}
            {!isPaid && paymentMethods.length > 0 && (
              <div className="bg-white rounded-lg shadow-brand-sm border border-stone p-5 space-y-4">
                <h3 className="font-display text-h3 text-midnight font-semibold">Payment Options</h3>

                {/* Amount selection */}
                {showAmountOptions && (
                  <div className="space-y-2">
                    <span className="block text-[11px] font-bold uppercase tracking-widest text-slate">
                      How much would you like to pay?
                    </span>
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedAmount('due_now')}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors text-left
                          ${selectedAmount === 'due_now'
                            ? 'border-plum bg-plum/5'
                            : 'border-stone bg-white hover:border-plum/40'}`}
                      >
                        <div>
                          <p className={`text-[14px] font-semibold ${selectedAmount === 'due_now' ? 'text-plum' : 'text-midnight'}`}>
                            Due now
                          </p>
                          <p className="text-[12px] text-slate">Next scheduled payment</p>
                        </div>
                        <span className={`text-[16px] font-bold ${selectedAmount === 'due_now' ? 'text-plum' : 'text-midnight'}`}>
                          {formatCents(dueNow)}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedAmount('remaining')}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors text-left
                          ${selectedAmount === 'remaining'
                            ? 'border-plum bg-plum/5'
                            : 'border-stone bg-white hover:border-plum/40'}`}
                      >
                        <div>
                          <p className={`text-[14px] font-semibold ${selectedAmount === 'remaining' ? 'text-plum' : 'text-midnight'}`}>
                            Remaining balance
                          </p>
                          <p className="text-[12px] text-slate">Pay off entire balance</p>
                        </div>
                        <span className={`text-[16px] font-bold ${selectedAmount === 'remaining' ? 'text-plum' : 'text-midnight'}`}>
                          {formatCents(remainingBalance)}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Stripe methods first (instant) */}
                {stripeMethods.map((m) => (
                  <a
                    key={m.type}
                    href={m.stripePaymentUrl ?? '#'}
                    className={`flex items-center justify-center gap-2 w-full btn-base text-white ${METHOD_COLORS.stripe} hover:opacity-90 min-h-[48px]`}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    {m.label}
                  </a>
                ))}

                {/* Offline methods section */}
                {offlineMethods.length > 0 && (
                  <>
                    {/* Disclaimer banner */}
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex gap-2">
                      <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-small text-blue-800">
                        If you pay using one of the methods below, your balance will not be updated
                        immediately. {org.name} will update it once they confirm receipt of your payment.
                      </p>
                    </div>

                    {/* Offline method list */}
                    {offlineMethods.map((m) => (
                      <OfflineMethodRow
                        key={m.type}
                        method={m}
                        amountCents={payAmountCents}
                        customerCompanyName={customer.companyName}
                        onDeepLinkClick={handleDeepLinkClick}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right column: Customer & Move details */}
          <div className="space-y-4">
            {/* Customer info card */}
            <div className="bg-white rounded-lg shadow-brand-sm border border-stone p-5">
              <h3 className="font-display text-h3 text-midnight font-semibold mb-3">Customer</h3>
              <dl className="space-y-2">
                {customer.name && (
                  <div>
                    <dt className="field-label">Name</dt>
                    <dd className="text-body text-midnight">{customer.name}</dd>
                  </div>
                )}
                {customer.companyName && (
                  <div>
                    <dt className="field-label">Company</dt>
                    <dd className="text-body text-midnight">{customer.companyName}</dd>
                  </div>
                )}
                {customer.phone && (
                  <div>
                    <dt className="field-label">Phone</dt>
                    <dd className="text-body text-midnight">
                      <a href={`tel:${customer.phone}`} className="text-plum underline">{customer.phone}</a>
                    </dd>
                  </div>
                )}
                {customer.email && (
                  <div>
                    <dt className="field-label">Email</dt>
                    <dd className="text-body text-midnight">
                      <a href={`mailto:${customer.email}`} className="text-plum underline">{customer.email}</a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Move details card */}
            <div className="bg-white rounded-lg shadow-brand-sm border border-stone p-5">
              <h3 className="font-display text-h3 text-midnight font-semibold mb-3">Move Details</h3>
              <dl className="space-y-2">
                {job.originAddress && (
                  <div>
                    <dt className="field-label">Origin</dt>
                    <dd className="text-body text-midnight">{job.originAddress}</dd>
                  </div>
                )}
                {job.destinationAddress && (
                  <div>
                    <dt className="field-label">Destination</dt>
                    <dd className="text-body text-midnight">{job.destinationAddress}</dd>
                  </div>
                )}
                {job.moveDate && (
                  <div>
                    <dt className="field-label">Move Date</dt>
                    <dd className="text-body text-midnight">{job.moveDate}</dd>
                  </div>
                )}
                {job.status && (
                  <div>
                    <dt className="field-label">Status</dt>
                    <dd className="text-body text-midnight">{humanStatus(job.status)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation modal for deep-link methods */}
      <DeepLinkModal
        isOpen={modalMethod !== null}
        method={modalMethod}
        deepLinkUrl={modalDeepLinkUrl}
        orgName={org.name}
        onConfirm={() => setModalMethod(null)}
        onClose={() => setModalMethod(null)}
      />
    </div>
  );
}

// ── Offline Method Row ───────────────────────────────────────────────────────

function OfflineMethodRow({
  method,
  amountCents,
  customerCompanyName,
  onDeepLinkClick,
}: {
  method: PublicPaymentMethod;
  amountCents: number;
  customerCompanyName?: string;
  onDeepLinkClick: (m: PublicPaymentMethod) => void;
}) {
  const name = methodDisplayName(method.type);

  // Deep-linked methods: Venmo, CashApp, PayPal — build link client-side with selected amount
  if (DEEP_LINK_METHODS.includes(method.type) && method.identifier) {
    const deepLink = buildDeepLink(method, amountCents, `Payment for ${customerCompanyName ?? 'move'}`);
    if (deepLink) {
      return (
        <div className="border border-stone rounded-md p-3 space-y-2">
          <div className="flex items-center gap-2">
            <PaymentMethodLogo method={method.type as PaymentMethodKey} size={24} />
            <span className="text-small text-slate">{name}:</span>
            <span className="text-body text-midnight font-semibold">{method.identifier}</span>
          </div>
          <button
            type="button"
            onClick={() => onDeepLinkClick(method)}
            className={`w-full btn-base text-white ${METHOD_COLORS[method.type] ?? 'bg-plum'} hover:opacity-90 min-h-[48px]`}
          >
            {method.label}
          </button>
        </div>
      );
    }
  }

  // Zelle: show identifier + copy
  if (method.type === 'zelle' && method.identifier) {
    return (
      <div className="border border-stone rounded-md p-3">
        <div className="flex items-center gap-2 mb-2">
          <PaymentMethodLogo method="zelle" size={24} />
          <span className="text-small text-slate">{name}:</span>
          <span className="text-body text-midnight font-semibold">{method.identifier}</span>
        </div>
        <CopyButton text={method.identifier} />
      </div>
    );
  }

  // Check
  if (method.type === 'check') {
    return (
      <div className="border border-stone rounded-md p-3">
        <span className="text-body text-midnight">
          Make payable to: <strong>{method.identifier ?? ''}</strong>
        </span>
      </div>
    );
  }

  // Cash
  if (method.type === 'cash') {
    return (
      <div className="border border-stone rounded-md p-3">
        <span className="text-body text-midnight">Cash accepted — pay your mover directly</span>
      </div>
    );
  }

  // Custom
  if (method.type === 'custom') {
    return (
      <div className="border border-stone rounded-md p-3">
        <p className="text-small text-slate mb-1">{method.label}</p>
        {method.identifier && (
          <p className="text-body text-midnight">{method.identifier}</p>
        )}
      </div>
    );
  }

  return null;
}
